import React, { useState, useEffect } from 'react';
import { HEALTH_STATES } from '@/const';
import { cn } from '@/lib/utils';
import { MIME_TYPE } from '@/const';
import { useLazyGetResourceQuery } from '@/store/api/diagnosticApi';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@/components';
import { NotificationAlert, NotificationAttachment } from '@/data/notifications';
import { useToast } from '@/hooks/shared/use-toast';
import { colors, HealthSeverity } from '@/constants/colors';

const renderAttachment = (type: string, attachment: string) => {
  switch (type) {
    case MIME_TYPE.Image_JPG:
      return <img src={`data:${type};base64, ${attachment}`} />;
    case MIME_TYPE.Image_PNG:
      return <img src={`data:${type};base64, ${attachment}`} />;
    case MIME_TYPE.Video_MP4:
      return (
        <video controls autoPlay loop>
          <source type={type} src={`data:${type};base64, ${attachment}`} />
        </video>
      );
    default:
      return null;
  }
};

interface GalleryActiveAttachmentProps {
  isFetching: boolean;
  resources: NotificationAttachment[];
  activeResource: number;
}

const GalleryActiveAttachment = ({ isFetching, resources, activeResource }: GalleryActiveAttachmentProps) => {
  return (
    <>
      {!isFetching && !!resources.length ? (
        <div>{renderAttachment(resources[activeResource]?.type, resources[activeResource]?.content)}</div>
      ) : (
        <div className="h-full w-full flex justify-center items-center pb-16">
          <Spinner size="large" />
        </div>
      )}
    </>
  );
};

interface NotificationModalTitleProps {
  notification: NotificationAlert;
}

export const NotificationModalTitle = ({ notification }: NotificationModalTitleProps) => {
  const Icon = HEALTH_STATES[notification.severity].icon;

  return (
    <div className="flex items-center gap-2">
      <div>
        <div className="w-10 h-10 bg-[#EDF2F7] flex justify-center items-center rounded-full">
          <Icon className={`text-[${colors.health[notification.severity as HealthSeverity]}] w-6 h-6`} />
        </div>
      </div>

      <div className="font-semibold">
        {notification.asset_ui_name}
        {notification.component_name ? ` / ${notification.component_name}` : null} – {notification.event_ui_name}
      </div>
    </div>
  );
};

interface NotificationModalContentProps {
  notification: NotificationAlert;
}

export const NotificationModalContent = ({ notification }: NotificationModalContentProps) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [fetchResource, { isFetching }] = useLazyGetResourceQuery();
  const [resources, setResources] = useState<NotificationAttachment[]>([]);
  const [activeResource, setActiveResource] = useState(0);

  useEffect(() => {
    (async () => {
      const promises = notification.attachments.map((attachment) => {
        return fetchResource(
          {
            resource_id: attachment.content,
          },
          false
        ).unwrap();
      });

      const resourcesArray: NotificationAttachment[] = [];

      const results = await Promise.allSettled(promises);

      results.forEach((item) => {
        if (item.status === 'fulfilled') {
          resourcesArray.push(item.value as NotificationAttachment);
        } else {
          toast({
            variant: 'destructive',
            title: `Failed load notification attachment`,
            description: t('general.errors.communication'),
          });
        }
      });

      setResources(resourcesArray);
    })();
  }, [notification]);

  return (
    <>
      {!!notification.content && <div className="py-4">{notification.content}</div>}
      {!!notification.attachments.length && (
        <GalleryActiveAttachment isFetching={isFetching} resources={resources} activeResource={activeResource} />
      )}
      {!isFetching && (
        <div className="flex gap-2">
          {!!resources.length &&
            resources.map((resource, index) => {
              return (
                <div
                  className={cn(
                    'max-w-[100px] my-2 p-1 cursor-pointer',
                    index === activeResource ? 'border-2 border-black' : ''
                  )}
                  key={index}
                  onClick={() => {
                    setActiveResource(index);
                  }}
                >
                  {renderAttachment(resource.type, resource.content)}
                </div>
              );
            })}
        </div>
      )}
    </>
  );
};
